import React, { useRef, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/joy';
import { motion } from 'framer-motion';
import { formatTitle } from '../../helpers/policyUtils';

/**
 * Wizard step navigation component
 * Displays a horizontal stepper with clickable steps using Framer Motion for smooth animations
 */
const WizardStepper = React.memo(({
  currentStructure,
  stepIndex,
  setStepIndex,
  setSelectedIndex
}) => {
  const scrollContainerRef = useRef(null);
  const activeStepRef = useRef(null);

  // Auto-scroll to active step
  useEffect(() => {
    if (activeStepRef.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const activeElement = activeStepRef.current;

      const containerWidth = container.offsetWidth;
      const activeLeft = activeElement.offsetLeft;
      const activeWidth = activeElement.offsetWidth;

      // Calculate scroll position to center the active element
      const targetScroll = activeLeft - (containerWidth / 2) + (activeWidth / 2);

      container.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    }
  }, [stepIndex]);

  const steps = [
    ...(currentStructure?.fields?.map((field, index) => ({
      id: index,
      label: formatTitle(field.key),
      type: 'field'
    })) || []),
    {
      id: currentStructure?.fields?.length || 0,
      label: 'Settings',
      type: 'settings'
    }
  ];

  return (
    <Box
      sx={{
        width: '100%',
        overflow: 'hidden',
        position: 'relative',
        py: 1
      }}
    >
      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          maskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)',
          px: 2,
          alignItems: 'center',
          justifyContent: 'flex-start', // Start from left, can be center if few items
        }}
      >
        {steps.map((step) => {
          const isActive = stepIndex === step.id;

          return (
            <Box
              key={step.id}
              ref={isActive ? activeStepRef : null}
              onClick={() => {
                if (step.type === 'field') {
                  setSelectedIndex(0);
                }
                setStepIndex(step.id);
              }}
              sx={{
                position: 'relative',
                cursor: 'pointer',
                px: 2,
                py: 0.5,
                borderRadius: '20px',
                flexShrink: 0,
                transition: 'color 0.2s',
                color: isActive ? 'neutral.800' : 'neutral.300',
                '&:hover': {
                  color: isActive ? 'neutral.800' : 'neutral.100',
                },
                zIndex: 1,
              }}
            >
              {isActive && (
                <motion.div
                  layoutId="activeStepIndicator"
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30
                  }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'var(--joy-palette-neutral-100)',
                    borderRadius: '20px',
                    zIndex: -1,
                  }}
                />
              )}
              <Typography
                level="title-sm"
                fontWeight={isActive ? 'lg' : 'md'}
                textColor="inherit"
              >
                {step.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
});

WizardStepper.displayName = 'WizardStepper';

export default WizardStepper;
